import React from "react";
import { toast } from "sonner";
import { Globe } from "lucide-react";
import { useBookingGate } from "../../lib/bookingGate";

/**
 * BookDirectOnSite — "Book direct on site" button.
 *
 * Two-mode behaviour:
 *   • When we have `websiteUrl` on file → direct link to it.
 *   • When we DON'T have it → fall back to a Google search for
 *     "<campsite name> <region> camping official site". User lands on
 *     Google with the top result almost always being the official
 *     website. We also copy the campsite name to clipboard so they can
 *     paste-search on Google's homepage if needed.
 *
 * Why this matters: ~6,500 of our 14k sites don't have a website on file
 * (mostly OSM-imported small farm pitches / bothies). Hiding the button
 * entirely on those sites tells the user "no way to book direct" — which
 * is misleading. Showing a fallback Google-search route gives every
 * camper a clear path to the official site.
 *
 * Gated by useBookingGate so anonymous taps bounce to /register first.
 */
export default function BookDirectOnSite({ websiteUrl, campsiteName, campsiteRegion, testId = "book-direct-site" }) {
  const gate = useBookingGate();
  const hasUrl = Boolean(websiteUrl);

  // Google search URL — fallback path when we don't have the website on file.
  // Quote the name to force exact-match, append region + "campsite" so
  // Google's top result is overwhelmingly the official site.
  const fallbackUrl = (() => {
    const parts = [
      `"${campsiteName}"`,
      campsiteRegion || "",
      "campsite",
      "uk",
    ].filter(Boolean).join(" ");
    return `https://www.google.com/search?q=${encodeURIComponent(parts)}&btnI=1`;
  })();

  const href = hasUrl ? websiteUrl : fallbackUrl;

  const handleClick = (e) => {
    gate(e, { reason: "book this campsite direct" });
    if (e.defaultPrevented) return;
    // For the fallback (Google) path, also copy the name to clipboard so
    // the user can paste if Google decides to show a search page instead
    // of feeling-lucky 302'ing.
    if (!hasUrl && campsiteName && navigator.clipboard?.writeText) {
      try {
        navigator.clipboard.writeText(campsiteName).catch(() => {});
        toast.info(`"${campsiteName}" copied — Google will find the official site`, { duration: 4500 });
      } catch {}
    }
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className="inline-flex items-center justify-center w-full px-4 py-4 bg-[#1f5fbf] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#194f9b] active:translate-y-[1px] transition-colors gap-2 shadow-[0_2px_0_0_#0e3b7a]"
      data-testid={hasUrl ? testId : `${testId}-fallback`}
      title={hasUrl ? `Book direct on ${campsiteName}'s own website` : `Find ${campsiteName}'s official site via Google`}
    >
      <Globe className="w-4 h-4 shrink-0" strokeWidth={2.25} />
      {hasUrl ? "Book direct on site" : "Find official site"}
      <span className="text-[11px] opacity-70">↗</span>
    </a>
  );
}
